import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://yoybuy.cam';

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'YOYBUY Spreadsheet: Полное руководство по системе покупок из Китая через таблицы',
  description: 'Подробное руководство по использованию YOYBUY Spreadsheet для покупок на Taobao и Weidian. Узнайте о преимуществах системы, сравнении с другими сервисами и способах экономии на доставке.',
  datePublished: '2026-07-05',
  dateModified: '2026-07-05',
  author: {
    '@type': 'Organization',
    name: 'YOYBUY CAM',
  },
  publisher: {
    '@type': 'Organization',
    name: 'YOYBUY CAM',
    url: siteUrl,
  },
  inLanguage: 'ru',
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${siteUrl}/yoybuy-spreadsheet-guide`,
  },
};

export default function ArticlePage() {
  const { t } = useTranslation();

  const relatedArticles = [
    {
      title: 'Как сэкономить на доставке при заказе из Китая через YOYBUY Spreadsheet',
      tag: 'Советы',
      image: 'https://readdy.ai/api/search-image?query=International%20shipping%20logistics%20concept%20with%20cardboard%20boxes%20and%20world%20map%20on%20clean%20cream%20desk%2C%20soft%20warm%20lighting%2C%20minimalist%20business%20photography%2C%20fresh%20aesthetic%2C%20warm%20neutral%20tones%2C%20professional%20composition%2C%20high%20detail&width=800&height=500&seq=yoybuy-rel-01&orientation=landscape',
    },
    {
      title: 'Taobao и Weidian: как находить лучшие товары через YOYBUY таблицы',
      tag: 'Поиск товаров',
      image: 'https://readdy.ai/api/search-image?query=Modern%20laptop%20displaying%20shopping%20interface%20on%20minimalist%20wooden%20desk%20with%20warm%20cafe%20lighting%2C%20clean%20workspace%20scene%2C%20soft%20natural%20light%2C%20fresh%20aesthetic%2C%20warm%20neutral%20background%2C%20editorial%20photography%2C%20high%20detail&width=800&height=500&seq=yoybuy-rel-02&orientation=landscape',
    },
    {
      title: 'Безопасность покупок: как YOYBUY Spreadsheet защищает ваши заказы',
      tag: 'Безопасность',
      image: 'https://readdy.ai/api/search-image?query=Secure%20shopping%20concept%20with%20shield%20icon%20and%20credit%20card%20on%20clean%20cream%20surface%2C%20soft%20warm%20studio%20lighting%2C%20minimalist%20photography%2C%20fresh%20aesthetic%2C%20warm%20neutral%20tones%2C%20professional%20composition%2C%20high%20detail&width=800&height=500&seq=yoybuy-rel-03&orientation=landscape',
    },
  ];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <Navbar />
      <main>
        <article>
          <header className="relative w-full h-[50vh] md:h-[60vh] flex items-end overflow-hidden">
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=Minimalist%20workspace%20with%20open%20laptop%20displaying%20spreadsheet%20interface%20on%20warm%20wooden%20desk%2C%20soft%20natural%20light%20from%20window%2C%20clean%20modern%20aesthetic%2C%20cream%20and%20beige%20neutral%20tones%2C%20editorial%20photography%2C%20warm%20atmosphere%2C%20professional%20composition%2C%20high%20detail&width=1600&height=700&seq=yoybuy-article-hero&orientation=landscape')`,
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
            <div className="relative z-10 w-full px-4 md:px-6 pb-12 md:pb-20">
              <div className="max-w-4xl">
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 leading-tight">
                  {t('title', 'YOYBUY Spreadsheet: Полное руководство по системе покупок из Китая через таблицы')}
                </h1>
                <div className="flex items-center gap-6 mt-6">
                  <span className="text-sm text-background-50/70">
                    {t('meta.date', '5 июля 2026')}
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-background-50/15 text-background-50/90 text-xs font-medium whitespace-nowrap">
                    {t('meta.category', 'Руководство')}
                  </span>
                  <span className="text-sm text-background-50/70">
                    {t('meta.readTime', '12 мин чтения')}
                  </span>
                </div>
              </div>
            </div>
          </header>

          <div className="bg-background-50 py-12 md:py-20">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-[720px] mx-auto">
                <nav className="mb-12">
                  <Link
                    to="/"
                    className="text-sm text-foreground-500 hover:text-primary-500 transition-colors duration-200"
                  >
                    {t('backToHome', '← Вернуться на главную YOYBUY CAM')}
                  </Link>
                </nav>

                <div className="text-base md:text-lg text-foreground-800 leading-relaxed space-y-8">
                  <p>
                    {t('content.intro', '')}
                  </p>

                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 pt-4">
                    {t('content.section1Title', 'Что такое YOYBUY Spreadsheet?')}
                  </h2>
                  <p>
                    {t('content.section1Content1', '')}
                  </p>
                  <p>
                    {t('content.section1Content2', '')}
                    Всё это доступно на сайте{' '}
                    <a
                      href="https://kaspreadsheet.com"
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-primary-500 hover:text-primary-600 underline transition-colors duration-200"
                    >
                      YOYBUY Spreadsheet
                    </a>
                    , где вы можете начать покупки прямо сейчас.
                  </p>

                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 pt-4">
                    {t('content.section2Title', 'Преимущества системы YOYBUY Spreadsheet')}
                  </h2>
                  <p>
                    {t('content.section2Content1', '')}
                  </p>
                  <p>
                    {t('content.section2Content2', '')}
                  </p>

                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 pt-4">
                    {t('content.section3Title', 'Как начать использовать YOYBUY Spreadsheet')}
                  </h2>
                  <p>
                    {t('content.section3Content1', '')}
                  </p>
                  <p>
                    {t('content.section3Content2', '')}
                    Подробнее о системе читайте на{' '}
                    <Link
                      to="/"
                      className="text-primary-500 hover:text-primary-600 underline transition-colors duration-200"
                    >
                      главной странице YOYBUY CAM
                    </Link>
                    .
                  </p>

                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 pt-4">
                    {t('content.section4Title', 'Сравнение YOYBUY Spreadsheet с другими сервисами')}
                  </h2>
                  <p>
                    {t('content.section4Content1', '')}
                  </p>
                  <p>
                    {t('content.section4Content2', '')}
                  </p>

                  <div className="bg-accent-50/40 rounded-2xl p-8 my-12">
                    <p className="text-lg font-heading font-bold text-foreground-950 mb-3">
                      Вывод
                    </p>
                    <p className="text-base text-foreground-700 leading-relaxed">
                      {t('content.conclusion', '')}
                      Посетите{' '}
                      <Link
                        to="/"
                        className="text-primary-500 hover:text-primary-600 underline transition-colors duration-200"
                      >
                        yoybuy.cam
                      </Link>{' '}
                      и начните свой путь в мир удобных покупок из Китая уже сегодня.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <section className="bg-background-100 py-16 md:py-24">
            <div className="w-full px-4 md:px-6">
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-0 mb-12">
                <div className="lg:w-1/2">
                  <p className="text-sm text-foreground-400 font-semibold uppercase tracking-wider mb-3">
                    {t('relatedTitle', 'Похожие статьи')}
                  </p>
                  <h2 className="text-2xl md:text-4xl font-heading font-bold italic text-foreground-950">
                    {t('relatedSubtitle', 'Другие полезные руководства')}
                  </h2>
                </div>
                <div className="lg:w-1/2 flex items-end">
                  <p className="text-sm text-foreground-600 leading-relaxed max-w-md">
                    Изучите другие материалы о системе YOYBUY Spreadsheet и онлайн-покупках из Китая
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((article, idx) => (
                  <Link
                    key={idx}
                    to="/yoybuy-spreadsheet-guide"
                    className="group rounded-2xl bg-background-50 overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer block"
                  >
                    <div className="w-full h-[200px] overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-5">
                      <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-3 whitespace-nowrap">
                        {article.tag}
                      </span>
                      <h3 className="text-base md:text-lg font-semibold text-foreground-900 leading-snug group-hover:text-primary-500 transition-colors duration-200">
                        {article.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="bg-background-50 py-16 md:py-24">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-2xl mx-auto text-center">
                <p className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-6">
                  Готовы начать покупки?
                </p>
                <p className="text-base text-foreground-600 mb-8">
                  Присоединяйтесь к тысячам покупателей, которые уже выбрали YOYBUY Spreadsheet
                </p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-3 px-10 py-4 rounded-full bg-foreground-950 text-background-50 font-semibold text-base whitespace-nowrap transition-all duration-300 hover:bg-foreground-800 hover:scale-105 cursor-pointer"
                >
                  Перейти на YOYBUY Sheet
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line"></i>
                  </span>
                </a>
              </div>
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </>
  );
}